import { useEffect } from 'react'
import {
  useGetAnalytics,
  useGetOrderPredictions,
  useGetCachedForecast,
  useGetForecastAccuracy,
} from '../hooks/backend/dashboardPage'
import { Button } from '../lib/shadcn/button'
import { cn } from '../lib/shadcn/utils'
import { RefreshCw, AlertCircle, BarChart3 } from 'lucide-react'
import OrderVolumeChart, { type OrderVolumePoint } from './ui/OrderVolumeChart'
import FulfillmentTimeChart, { type FulfillmentPoint } from './ui/FulfillmentTimeChart'
import OrdersWaitingCard, { type WaitingOrder } from './ui/OrdersWaitingCard'
import TopCustomersChart, { type TopCustomerPoint } from './ui/TopCustomersChart'
import TopProductsChart, { type TopProductPoint } from './ui/TopProductsChart'
import FlavorTrendsCard, { type FlavorTrend } from './ui/FlavorTrendsCard'
import OrderForecastPanel, { type ForecastData } from './ui/OrderForecastPanel'
import ForecastAccuracyCard, { type ForecastAccuracyData } from './ui/ForecastAccuracyCard'
import ProfitabilityPanel from './ui/ProfitabilityPanel'
import TodayPanel from './ui/TodayPanel'
import CustomerInboxSummary from './ui/CustomerInboxSummary'

interface AnalyticsData {
  orderVolume: OrderVolumePoint[]
  fulfillmentTime: FulfillmentPoint[]
  waitingOrders: WaitingOrder[]
  topCustomers: TopCustomerPoint[]
  topProducts: TopProductPoint[]
  flavorTrends: FlavorTrend[]
}

export default function DashboardPage() {
  const getAnalytics = useGetAnalytics()
  const getPredictions = useGetOrderPredictions()
  const getCachedForecast = useGetCachedForecast()
  const getAccuracy = useGetForecastAccuracy()

  useEffect(() => {
    getAnalytics.trigger()
    getCachedForecast.trigger()
    getAccuracy.trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const analytics = getAnalytics.data as AnalyticsData | undefined
  const analyticsLoading = getAnalytics.loading && !analytics

  // Show the freshly regenerated forecast if the user just made one this
  // session; otherwise fall back to the cached copy loaded on mount.
  const predictions = (getPredictions.data ?? getCachedForecast.data) as ForecastData | undefined
  const predictionsLoading = (getCachedForecast.loading || getPredictions.loading) && !predictions
  const predictionsError = getPredictions.error ?? getCachedForecast.error

  const refreshAll = () => {
    getAnalytics.trigger(undefined, { skipCache: true })
    getAccuracy.trigger(undefined, { skipCache: true })
  }

  return (
    <div className="p-4 sm:p-6 2xl:p-10 max-w-7xl 2xl:max-w-none mx-auto space-y-6 2xl:space-y-10">
      <TodayPanel />
      <CustomerInboxSummary />

      <div className="flex items-center justify-between gap-4 pt-2">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            Analytics
          </h1>
          <p className="text-sm text-muted-foreground">
            Order trends, fulfillment performance, profit, and an AI-generated demand forecast
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refreshAll}
          disabled={getAnalytics.loading}
        >
          <RefreshCw
            className={cn('w-4 h-4 mr-2', getAnalytics.loading && 'animate-spin')}
          />
          Refresh
        </Button>
      </div>

      {getAnalytics.error && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {getAnalytics.error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <FulfillmentTimeChart data={analytics?.fulfillmentTime ?? []} loading={analyticsLoading} />
        <OrdersWaitingCard data={analytics?.waitingOrders ?? []} loading={analyticsLoading} />
      </div>

      <OrderVolumeChart data={analytics?.orderVolume ?? []} loading={analyticsLoading} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopCustomersChart data={analytics?.topCustomers ?? []} loading={analyticsLoading} />
        <TopProductsChart data={analytics?.topProducts ?? []} loading={analyticsLoading} />
      </div>

      <FlavorTrendsCard data={analytics?.flavorTrends ?? []} loading={analyticsLoading} />

      <ProfitabilityPanel />

      <OrderForecastPanel
        data={predictions}
        loading={predictionsLoading}
        refreshing={getPredictions.loading && !!predictions}
        error={predictionsError}
        onRefresh={() => getPredictions.trigger(undefined, { skipCache: true })}
      />

      <ForecastAccuracyCard
        data={getAccuracy.data as ForecastAccuracyData | undefined}
        loading={getAccuracy.loading && !getAccuracy.data}
        error={getAccuracy.error}
      />
    </div>
  )
}
